import React from 'react';
import { useLocation } from 'react-router-dom';
import SEOHead from './SEOHead';
import { DCKSLAP_DAILY_WITHDRAWAL_LIMIT } from '../../constants';
import { isSwapHostname } from '../../utils/hostRouting';

/*
 * Route-level SEO configuration for all Reddicks pages.
 * Priority: noindex routes > DckSlap routes > main site routes.
 */

const ROUTE_SEO = {
  '/': {
    title: 'Reddicks on Base - Locked and Live',
    description:
      'Base-first REDDICKS landing page with trust-first messaging, buy routes, chart access, and clear lock-oriented positioning.',
    breadcrumbs: [{ name: 'Home', path: '/' }],
  },
  '/home': {
    title: 'Reddicks - Landing',
    description:
      'Landing alias for REDDICKS with Base-first trust messaging and a clear path into live Radix utility.',
    canonicalPath: '/',
    breadcrumbs: [
      { name: 'Home', path: '/' },
      { name: 'Landing', path: '/home' },
    ],
  },
  '/whyreddicks': {
    title: 'REDDICKS Story Update - In Progress',
    description:
      'The REDDICKS narrative section is being refreshed. Use verify and buy pages for current trust and route context.',
    breadcrumbs: [
      { name: 'Home', path: '/' },
      { name: 'Story Update', path: '/whyreddicks' },
    ],
  },
  '/tokenomics': {
    title: 'Tokenomics - Circulating Supply and Lock Transparency',
    description:
      'Full REDDICKS tokenomics breakdown: circulating supply, lock coverage, Rug Proof POL framing, and on-chain verification links.',
    breadcrumbs: [
      { name: 'Home', path: '/' },
      { name: 'Tokenomics', path: '/tokenomics' },
    ],
  },
  '/buy': {
    title: 'Buy REDDICKS on Base - Routes and Verification',
    description:
      'Buy REDDICKS with clear liquidity context across Base and Radix, including lock-first trust anchors and public verification links.',
    breadcrumbs: [
      { name: 'Home', path: '/' },
      { name: 'Buy', path: '/buy' },
    ],
  },
  '/swap': {
    title: 'Swap Explorer - REDDICKS Trading Workspace',
    description:
      'Explore REDDICKS and Radix-native pairs from a dedicated trading workspace with a live chart pane, official REDDICKS swap rail, and on-ledger utility intel.',
    canonicalPath: '/swap',
    breadcrumbs: [
      { name: 'Home', path: '/' },
      { name: 'Swap', path: '/swap' },
    ],
  },
  '/verify': {
    title: 'Verify Liquidity - REDDICKS Trust Portal',
    description:
      'Verify REDDICKS liquidity and trust signals across Radix and Base with live links, pending status labels, and plain-language checks.',
    breadcrumbs: [
      { name: 'Home', path: '/' },
      { name: 'Verify Liquidity', path: '/verify' },
    ],
  },
  '/stake': {
    title: 'Stake DCKS on RSwap - Weekly Rewards',
    description:
      'Stake DCKS on RSwap and get your share of 48,300 DCKS paid out each week.',
    breadcrumbs: [
      { name: 'Home', path: '/' },
      { name: 'Stake', path: '/stake' },
    ],
  },
  '/validator': {
    title: 'Reddicks Validator Node - Secure Radix',
    description:
      'Delegate XRD to the official Reddicks validator. A 20% fee is applied, remaining rewards are used to buy DCKS, and payouts are sent to stakers on the first Monday of each month.',
    breadcrumbs: [
      { name: 'Home', path: '/' },
      { name: 'Validator', path: '/validator' },
    ],
  },
  '/game-info': {
    title: 'Reddicks Games - Play and Earn on Radix',
    description:
      'Explore Reddicks games including DckSlap: mint NFTs, earn rewards, and engage with utility beyond trading.',
    breadcrumbs: [
      { name: 'Home', path: '/' },
      { name: 'Games', path: '/game-info' },
    ],
  },
  '/memecollection': {
    title: 'Meme Collection - The Best of Reddicks',
    description:
      'The official Reddicks meme vault. Browse community-created content from the funniest memecoin universe on Radix.',
    breadcrumbs: [
      { name: 'Home', path: '/' },
      { name: 'Meme Collection', path: '/memecollection' },
    ],
  },
  '/charity': {
    title: 'Reddicks Charity - Endowment Model',
    description:
      'Reddicks charity follows an endowment model: grow principal through yield-bearing allocations and donate annual yield by DAO direction.',
    breadcrumbs: [
      { name: 'Home', path: '/' },
      { name: 'Charity', path: '/charity' },
    ],
  },
  '/treasury': {
    title: 'Reddicks Treasury - On-Chain Transparency',
    description:
      'Live treasury context for REDDICKS: holdings, strategic allocations, and long-term ecosystem funding signals.',
    breadcrumbs: [
      { name: 'Home', path: '/' },
      { name: 'Treasury', path: '/treasury' },
    ],
  },
  '/image-creator': {
    title: 'Reddicks Image Creator - Make Your Own Memes',
    description:
      'Create and share custom Reddicks memes with the community image creator.',
    breadcrumbs: [
      { name: 'Home', path: '/' },
      { name: 'Image Creator', path: '/image-creator' },
    ],
  },
};

const NOINDEX_ROUTES = {
  '/start': {
    title: 'Start Game',
    description: 'Start a new Reddicks game session.',
    robots: 'noindex, nofollow',
  },
  '/live-card': {
    title: 'Live Card',
    description: 'Reddicks live statistics card.',
    robots: 'noindex, nofollow',
  },
  '/dckslap/test-transaction': {
    title: 'Test Transaction',
    description: 'DckSlap test transaction page.',
    robots: 'noindex, nofollow',
  },
  '/dckslap/secret-machine': {
    title: 'Secret Machine - Coming Soon',
    description: 'Something secret is brewing in the DckSlap universe. Stay tuned.',
    robots: 'noindex, follow',
    breadcrumbs: [
      { name: 'Home', path: '/' },
      { name: 'DckSlap', path: '/dckslap' },
      { name: 'Secret Machine', path: '/dckslap/secret-machine' },
    ],
  },
};

const HOWTOPLAY_FAQ_SCHEMA = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: [
    {
      '@type': 'Question',
      name: 'What is DckSlap?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'DckSlap is a play-to-earn NFT game on Radix where you hold REDDICKS to mint unique DCKSLAP NFTs, earn GBOF rewards, and compete on the leaderboard.',
      },
    },
    {
      '@type': 'Question',
      name: 'How do I play DckSlap?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: `Hold at least 15,000 REDDICKS in your Radix wallet. User badges are distributed every 24 hours to eligible wallets. Once you have a badge, you can mint up to ${DCKSLAP_DAILY_WITHDRAWAL_LIMIT} DCKSLAPs daily.`,
      },
    },
    {
      '@type': 'Question',
      name: 'What are GBOFs?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'GBOFs (Great Balls of Fire) are reward tokens earned by depositing DCKSLAPs. They unlock access to the Candy Shop, VIP Lounge, and other exclusive features.',
      },
    },
    {
      '@type': 'Question',
      name: 'How much does it cost to play DckSlap?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: `Each DCKSLAP costs 20 REDDICKS to mint. You must hold at least 15,000 REDDICKS and can mint up to ${DCKSLAP_DAILY_WITHDRAWAL_LIMIT} DCKSLAPs per day.`,
      },
    },
  ],
};

const DCKSLAP_SEO = {
  '/dckslap': {
    title: 'DckSlap - The Ultimate Slap Game on Radix',
    description:
      'Enter the DCKBase. Hold REDDICKS to mint DCKSLAPs, climb the leaderboard, earn GBOFs, and collect from the Candy Shop.',
    breadcrumbs: [
      { name: 'Home', path: '/' },
      { name: 'DckSlap', path: '/dckslap' },
    ],
  },
  '/dckslap/homepage': {
    title: 'DckSlap - The Ultimate Slap Game on Radix',
    description:
      'Enter the DCKBase. Hold REDDICKS to mint DCKSLAPs, climb the leaderboard, earn GBOFs, and collect from the Candy Shop.',
    canonicalPath: '/dckslap',
    breadcrumbs: [
      { name: 'Home', path: '/' },
      { name: 'DckSlap', path: '/dckslap' },
    ],
  },
  '/dckslap/slapgenerator': {
    title: 'Mint DCKSLAPs - Slap Generator',
    description:
      'Mint your daily DCKSLAPs by holding REDDICKS. Each slap is a unique NFT forged on Radix.',
    breadcrumbs: [
      { name: 'Home', path: '/' },
      { name: 'DckSlap', path: '/dckslap' },
      { name: 'Slap Generator', path: '/dckslap/slapgenerator' },
    ],
  },
  '/dckslap/sendslap': {
    title: 'Send a DCKSLAP - Share the Slapfest',
    description:
      'Send a DCKSLAP NFT to a friend (or enemy) and spread the slapfest across the Radix network.',
    breadcrumbs: [
      { name: 'Home', path: '/' },
      { name: 'DckSlap', path: '/dckslap' },
      { name: 'Send Slap', path: '/dckslap/sendslap' },
    ],
  },
  '/dckslap/leaderboard': {
    title: 'DCKSLAP Leaderboard - Top Slappers',
    description:
      'See who dominates the DCKSLAP leaderboard. Top slappers earn REDDICKS rewards.',
    breadcrumbs: [
      { name: 'Home', path: '/' },
      { name: 'DckSlap', path: '/dckslap' },
      { name: 'Leaderboard', path: '/dckslap/leaderboard' },
    ],
  },
  '/dckslap/howtoplay': {
    title: 'How To Play DckSlap - Rules and Guide',
    description:
      'Learn how to play DckSlap: hold REDDICKS, mint DCKSLAPs, earn GBOFs, and climb the leaderboard.',
    breadcrumbs: [
      { name: 'Home', path: '/' },
      { name: 'DckSlap', path: '/dckslap' },
      { name: 'How To Play', path: '/dckslap/howtoplay' },
    ],
    extraSchema: HOWTOPLAY_FAQ_SCHEMA,
  },
  '/dckslap/achievements': {
    title: 'GBOF Achievements - Great Balls of Fire',
    description:
      'Track your GBOF achievements in DckSlap. Earn Great Balls of Fire by slapping hard and depositing DCKSLAPs.',
    breadcrumbs: [
      { name: 'Home', path: '/' },
      { name: 'DckSlap', path: '/dckslap' },
      { name: 'Achievements', path: '/dckslap/achievements' },
    ],
  },
  '/dckslap/candyshop': {
    title: 'Candy Shop - DckSlap Rewards',
    description:
      'Play the DckSlap candy machine. Use GBOFs to win tokens, NFTs, and exclusive candy rewards.',
    breadcrumbs: [
      { name: 'Home', path: '/' },
      { name: 'DckSlap', path: '/dckslap' },
      { name: 'Candy Shop', path: '/dckslap/candyshop' },
    ],
  },
  '/dckslap/candywrapper': {
    title: 'Candy Wrapper - Seal Your DckSlap Candy',
    description:
      'Take loose DckSlap candy into the Candy Wrapper and seal it into collector-ready presentation.',
    breadcrumbs: [
      { name: 'Home', path: '/' },
      { name: 'DckSlap', path: '/dckslap' },
      { name: 'Candy Wrapper', path: '/dckslap/candywrapper' },
    ],
  },
  '/dckslap/viplounge': {
    title: 'VIP Lounge - Exclusive DckSlap Perks',
    description:
      'Access the VIP Lounge by depositing GBOFs. Exclusive perks, rewards, and bragging rights for top players.',
    breadcrumbs: [
      { name: 'Home', path: '/' },
      { name: 'DckSlap', path: '/dckslap' },
      { name: 'VIP Lounge', path: '/dckslap/viplounge' },
    ],
  },
  '/dckslap/candycombiner': {
    title: 'Fusion Chamber - Combine Candies',
    description:
      'Combine your DckSlap candies in the Fusion Chamber and preview upcoming merge mechanics.',
    breadcrumbs: [
      { name: 'Home', path: '/' },
      { name: 'DckSlap', path: '/dckslap' },
      { name: 'Fusion Chamber', path: '/dckslap/candycombiner' },
    ],
  },
};

const RouteSEO = () => {
  const location = useLocation();
  const path = location.pathname;
  const swapSeo = (path === '/swap' || (isSwapHostname() && path === '/')) ? ROUTE_SEO['/swap'] : null;
  const seo = swapSeo || NOINDEX_ROUTES[path] || DCKSLAP_SEO[path] || ROUTE_SEO[path];

  if (!seo) {
    return null;
  }

  return (
    <SEOHead
      title={seo.title}
      description={seo.description}
      path={seo.canonicalPath || path}
      robots={seo.robots}
      breadcrumbs={seo.breadcrumbs}
      extraSchema={seo.extraSchema}
      siteUrl={seo.siteUrl}
      canonicalUrl={seo.canonicalUrl}
    />
  );
};

export default RouteSEO;
